// ✅ Trekkin.app — IntroScreen v3.8.1 (FIX LOOP + BASE_URL)
// 🔹 Usa import.meta.env.BASE_URL para funcionar em /app
// 🔹 Efeito corre uma única vez (sem loop)
// 🔹 Garante apenas um timer / chamada a onClose
// 🔹 Fundo + logo com animação suave

import { useEffect, useRef, useState } from "react";
import { useTranslate } from "../i18n/useTranslate";
import LoadingDotsOverlay from "./LoadingDotsOverlay.jsx";

const base = import.meta.env.BASE_URL || "/";
const logoSrc = `${base}logo-trekkin.png`;

export default function IntroScreen({ onClose, appReady = false }) {
  const [assetsReady, setAssetsReady] = useState(false);
  const [mapReady, setMapReady] = useState(
    () => typeof window !== "undefined" && window.__TREKKIN_MAP_READY === true
  );
  const [mapBaseReady, setMapBaseReady] = useState(false);
  const onCloseRef = useRef(onClose);
  const introStartedAtRef = useRef(Date.now());
  const closeRequestedRef = useRef(false);
  const translate = useTranslate();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const logo = new Image();
    let settled = false;

    const handleLoad = () => {
      if (settled) return;
      settled = true;
      setAssetsReady(true);
    };

    logo.src = logoSrc;
    logo.onload = handleLoad;
    logo.onerror = handleLoad;

    return undefined;
  }, []);

  useEffect(() => {
    const detectMountedMap = () => {
      if (window.__TREKKIN_MAP_READY === true || window.__trekkinMap) {
        setMapReady(true);
      }
      const container = window.__trekkinMap?.getContainer?.();
      const loadedRaster = container?.querySelector?.(".leaflet-tile-loaded");
      const renderedCanvas = container?.querySelector?.(
        ".maplibregl-canvas, .mapboxgl-canvas"
      );
      if (
        loadedRaster ||
        (Number(renderedCanvas?.width || 0) > 0 &&
          Number(renderedCanvas?.height || 0) > 0)
      ) {
        setMapBaseReady(true);
      }
    };
    const handleMapReady = () => setMapReady(true);
    const handleMapBaseReady = () => setMapBaseReady(true);

    window.addEventListener("trekkin:map-ready", handleMapReady);
    window.addEventListener("trekkin:map-base-visible", handleMapBaseReady);
    const frame = window.requestAnimationFrame?.(detectMountedMap);
    const timer = window.setInterval(detectMountedMap, 250);
    return () => {
      window.removeEventListener("trekkin:map-ready", handleMapReady);
      window.removeEventListener("trekkin:map-base-visible", handleMapBaseReady);
      if (frame) window.cancelAnimationFrame?.(frame);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!(assetsReady && appReady && mapReady && mapBaseReady)) return undefined;

    const remaining = Math.max(
      0,
      900 - (Date.now() - introStartedAtRef.current)
    );
    const closeTimer = window.setTimeout(() => {
      if (closeRequestedRef.current) return;
      closeRequestedRef.current = true;
      console.log("✅ Aplicação montada — terminar loader inicial");
      onCloseRef.current?.();
    }, remaining);

    return () => window.clearTimeout(closeTimer);
  }, [appReady, assetsReady, mapBaseReady, mapReady]);

  useEffect(() => {
    const hardTimeout = window.setTimeout(() => {
      if (closeRequestedRef.current) return;
      closeRequestedRef.current = true;
      console.warn("⚠️ Loader inicial terminou por timeout de segurança.");
      onCloseRef.current?.();
    }, 12000);

    return () => window.clearTimeout(hardTimeout);
  }, []);

  return (
    <LoadingDotsOverlay
      text={translate("intro.loading")}
      zIndex={10000}
      variant="intro"
    />
  );
}
